/**
 * Shared service-spec rows editor — the VNET / bandwidth / shaper /
 * cosq grid used by the Pending tab's bulk provision and the site
 * Auto-provision template. Extracted from PendingTab so the two
 * surfaces can't drift.
 *
 * Controlled: the parent owns the rows (localStorage persistence,
 * server round-trip, self-heal effects all stay parent-side). Known
 * profile lists come from the chassis's parsed backup
 * (GET /chassis/{id}/profiles); a value not in its list renders the
 * "(not on OLT)" warning but is still submittable (SelectOrCustom's
 * Custom… path).
 */
import { Plus } from "lucide-react";

import { SelectOrCustom } from "../../components/SelectOrCustom";

export type ServiceSpecRow = {
  vnet_profile: string;
  upstream: string;
  downstream: string;
  cosq_profile: string;
};

export type KnownProfiles = {
  vnet: string[];
  bandwidth: string[];
  shaper: string[];
  cosq: string[];
};

export const notOnOlt = (value: string, known: string[]): boolean =>
  !!value && known.length > 0 && !known.includes(value);

/** "(not on OLT)" summary lines for a confirm dialog — one per field
 * whose value isn't in the chassis's parsed profile list. */
export function missingProfileWarnings(
  services: ServiceSpecRow[],
  known: KnownProfiles,
): string[] {
  const missing: string[] = [];
  services.forEach((s, idx) => {
    const label = `Service ${idx + 1}`;
    if (notOnOlt(s.vnet_profile, known.vnet))
      missing.push(`${label} VNET: ${s.vnet_profile}`);
    if (notOnOlt(s.upstream, known.bandwidth))
      missing.push(`${label} bandwidth: ${s.upstream}`);
    if (notOnOlt(s.downstream, known.shaper))
      missing.push(`${label} shaper: ${s.downstream}`);
    if (notOnOlt(s.cosq_profile, known.cosq))
      missing.push(`${label} COS: ${s.cosq_profile}`);
  });
  return missing;
}

const selectCls =
  "w-full rounded border border-slate-700 bg-slate-950 px-2 py-1 font-mono text-xs text-slate-100 focus-visible:border-emerald-700 focus-visible:outline-none";

export function ServiceSpecsEditor({
  services,
  onChange,
  known,
  defaultCosqProfile,
  defaultUpstream = "500M-burst",
  defaultDownstream = "1000M-burst",
  minRows = 1,
  noParsedProfiles = false,
}: {
  services: ServiceSpecRow[];
  onChange: (next: ServiceSpecRow[]) => void;
  known: KnownProfiles;
  /** Chassis-derived cosq default (most-used parsed cosq profile) —
   * used for new rows + the placeholder; "c0" only as last resort. */
  defaultCosqProfile: string | null;
  defaultUpstream?: string;
  defaultDownstream?: string;
  minRows?: number;
  /** True when the chassis has no parsed backup yet — dropdowns are
   * empty and values can't be validated. */
  noParsedProfiles?: boolean;
}) {
  const updateService = (idx: number, patch: Partial<ServiceSpecRow>) =>
    onChange(services.map((s, j) => (j === idx ? { ...s, ...patch } : s)));
  const addService = () =>
    onChange([
      ...services,
      {
        vnet_profile: "",
        upstream: defaultUpstream,
        downstream: defaultDownstream,
        cosq_profile: defaultCosqProfile ?? "c0",
      },
    ]);
  const removeService = (idx: number) =>
    onChange(
      services.length > minRows
        ? services.filter((_, j) => j !== idx)
        : services,
    );

  return (
    <div className="space-y-2">
      <div className="flex items-baseline justify-between">
        <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-300">
          Services
          {noParsedProfiles && (
            <span className="ml-2 normal-case text-[10px] text-amber-300">
              (no parsed profiles — take a backup + re-parse for dropdowns)
            </span>
          )}
        </h4>
        <button
          type="button"
          onClick={addService}
          className="inline-flex items-center gap-1 rounded border border-slate-700 px-2 py-0.5 text-[11px] text-slate-200 hover:bg-slate-800"
        >
          <Plus className="h-3 w-3" />
          Add service
        </button>
      </div>
      <div className="space-y-2">
        {services.map((s, i) => {
          const vnetMissing = notOnOlt(s.vnet_profile, known.vnet);
          const bwMissing = notOnOlt(s.upstream, known.bandwidth);
          const shMissing = notOnOlt(s.downstream, known.shaper);
          const cosMissing = notOnOlt(s.cosq_profile, known.cosq);
          return (
            <div
              key={i}
              className="space-y-1.5 rounded border border-slate-800/80 bg-slate-900/40 p-2"
            >
              <div className="flex items-center justify-between text-[11px]">
                <span className="font-semibold text-slate-300">
                  Service {i + 1}
                </span>
                <button
                  type="button"
                  onClick={() => removeService(i)}
                  disabled={services.length <= minRows}
                  className="rounded border border-red-800/60 px-1.5 py-0.5 text-[10px] text-red-300 hover:bg-red-900/30 disabled:opacity-30"
                >
                  Remove
                </button>
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <label className="space-y-1">
                  <span className="block text-slate-400">VNET Profile</span>
                  <SelectOrCustom
                    value={s.vnet_profile}
                    onChange={(v) => updateService(i, { vnet_profile: v })}
                    options={known.vnet}
                    placeholder="Pick a VNET…"
                    className={selectCls}
                  />
                  {vnetMissing && (
                    <span className="block text-[10px] text-amber-300">
                      “{s.vnet_profile}” (not on OLT)
                    </span>
                  )}
                </label>
                <label className="space-y-1">
                  <span className="block text-slate-400">
                    Upstream (Bandwidth)
                  </span>
                  <SelectOrCustom
                    value={s.upstream}
                    onChange={(v) => updateService(i, { upstream: v })}
                    options={known.bandwidth}
                    placeholder={`${defaultUpstream} (default)`}
                    className={selectCls}
                  />
                  {bwMissing && (
                    <span className="block text-[10px] text-amber-300">
                      “{s.upstream}” (not on OLT)
                    </span>
                  )}
                </label>
                <label className="space-y-1">
                  <span className="block text-slate-400">
                    Downstream (Shaper)
                  </span>
                  <SelectOrCustom
                    value={s.downstream}
                    onChange={(v) => updateService(i, { downstream: v })}
                    options={known.shaper}
                    placeholder={`${defaultDownstream} (default)`}
                    className={selectCls}
                  />
                  {shMissing && (
                    <span className="block text-[10px] text-amber-300">
                      “{s.downstream}” (not on OLT)
                    </span>
                  )}
                </label>
                <label className="space-y-1">
                  <span className="block text-slate-400">
                    COS Profile (cosq)
                  </span>
                  <SelectOrCustom
                    value={s.cosq_profile}
                    onChange={(v) => updateService(i, { cosq_profile: v })}
                    options={known.cosq}
                    placeholder={`${defaultCosqProfile ?? "c0"} (default)`}
                    className={selectCls}
                  />
                  {cosMissing && (
                    <span className="block text-[10px] text-amber-300">
                      “{s.cosq_profile}” (not on OLT)
                    </span>
                  )}
                </label>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
